import { Button, Stack, useMediaQuery } from '@mui/material'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import { FunctionComponent, useContext, useState } from 'react'
import { IFileDownload } from '../../../../shared/services/file-download/file-download.model'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import moment from 'moment'
import CreatePdfService from '../../../../shared/services/create-pdf/create-pdf.service'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../context/user-details/user-details'

export const ReportingProjectDownloadPdfTable: FunctionComponent<{
  fileDownloads: IFileDownload[]
  getDownloads: () => void
  projectId: string
  inputPage: number
  setInputPage: React.Dispatch<React.SetStateAction<number>>
  totalRecords: number
  perPage: number
  setPerPage: React.Dispatch<React.SetStateAction<number>>
}> = ({
  fileDownloads,
  getDownloads,
  projectId,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
}) => {
  const createPdfService = new CreatePdfService()
  const fileDownloadService = new FileDownloadService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))

  const [selected, setSelected] = useState<string[]>([])
  const [generating, setGenerating] = useState(false)

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'File Name', sortable: false, isHeading: true },
    { id: 'date', numeric: false, label: 'Created On', sortable: false },
  ]

  const rows = !fileDownloads ? [] : fileDownloads.map(download => ({
    id: download.id,
    name: download.filename,
    date: moment(download.downloadDate).format('DD/MM/YYYY, HH:mm'),
    action: (
      <TableActionButtons
        id={download.id}
        includeDownload={true}
        includeDelete={true}
        onDownload={() => downloadHandler(download.id, download.filename)}
        onDelete={() => deleteHandler(download.id)}
      />
    ),
  }))

  const downloadHandler = (downloadId: string, filename: string) => {
    fileDownloadService.downloadFile(downloadId, filename)
      .catch(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
  }

  const deleteHandler = (downloadId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this file? This action cannot be undone.',
      buttonCopy: 'Continue',
      cancelButtonCopy: 'Cancel',
    }).then(response => {
      if (response) {
        createPdfService.deleteReportPdf(`?fileDownloadId=${downloadId}`)
          .then(() => {
            if (inputPage > 0 && fileDownloads?.length === 1) {
              setInputPage(inputPage - 1)
            } else {
              getDownloads()
            }
            userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
          })
          .catch(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          })
      }
    })
  }

  const generateHandler = async () => {
    const companyName = userDetailsContext.userDetails?.organisation?.name
    if (!companyName || !projectId) return
    setGenerating(true)
    try {
      await createPdfService.createProjectReportPdf(companyName, `?projectId=${projectId}`)
      getDownloads()
      userNotificationsContext.openSnackbar({ open: true, message: 'PDF generated successfully' })
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error generating the PDF, please try again', backgroundColor: 'text.primary' })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction='row' justifyContent='flex-end'>
        <Button variant='contained' onClick={generateHandler} disabled={generating}>
          {generating ? 'Generating…' : 'Generate PDF'}
        </Button>
      </Stack>
      <TableDisplay
        isMobileMode={isMobileMode}
        headCells={headCells}
        rows={rows}
        handleSelected={(ids: string[]) => { if (ids[0]) downloadHandler(ids[0], fileDownloads.find(d => d.id === ids[0])?.filename ?? '') }}
        haveCheckboxSelect={false}
        selected={selected}
        setSelected={setSelected}
        selectByIcon={true}
        inputPage={inputPage}
        setInputPage={setInputPage}
        totalRecords={totalRecords}
        perPage={perPage}
        setPerPage={setPerPage}
      />
    </Stack>
  )
}
